import React, { useCallback, useState, useMemo, forwardRef } from 'react';
import { DropDownTree, MultiSelectTree } from '@progress/kendo-react-dropdowns';
import cn from 'classnames';
import isNil from 'lodash/isNil';
import { processTreeData, expandedState } from './tree-data-operations';
import { emptyArray } from 'src/shared/helpers/utils';

const ReadOnlyDropDownTree = ({ value, textField, ...props }) => {
  const className = cn('read-only', { empty: isNil(value) });
  const readOnlyValue = isNil(value) ? 'Not selected' : value[textField];
  return <div className={className} {...props}>{readOnlyValue}</div>;
};

/**
 * @typedef {React.ComponentProps<DropDownTree>} TelerikDropDownTreeProps
 * @typedef {React.ComponentProps<MultiSelectTree>} TelerikMultiSelectTreeProps
 *
 * @typedef {Object} AdditionalDropDownTreeProps
 * @property {string} [dataTestId] Data test Id.
 * @property {boolean} [multiSelect={false}] Indicates if a multi select is used. False by default.
 * @property {boolean} [multiExpand={true}] Indicates whether to allow multiple simultaneous expansions. True by default.
 *
 * @param {TelerikDropDownTreeProps & TelerikMultiSelectTreeProps & AdditionalDropDownTreeProps} props
 * @param {React.Ref<HTMLDivElement>} ref
 * @returns {React.ReactElement}
 */
const BaseDropDownTree = ({
  readOnly, multiSelect = false, data = emptyArray, multiExpand = true, dataTestId, onExpandChange, onChange, ...props
}, ref) => {
  const {
    value, selectField, textField, expandField, dataItemKey, filter, forceExpanded = emptyArray,
  } = props;

  // Expand
  const [expanded, setExpanded] = useState([data && data[0] && data[0][dataItemKey]]);

  const handleExpandChange = useCallback(
    (event) => {
      setExpanded(expandedState(event.item, dataItemKey, expanded, multiExpand));
      onExpandChange?.(event, dataItemKey);
    },
    [expanded, dataItemKey, multiExpand],
  );

  // Build tree data
  const treeData = useMemo(() => processTreeData(
    data,
    {
      expanded: expanded.concat(forceExpanded ?? []),
      value,
      filter,
    },
    {
      selectField,
      expandField,
      dataItemKey,
      subItemsField: 'items',
    },
  ),
  [data, expanded, value, filter, selectField, expandField, dataItemKey, forceExpanded]);

  if (readOnly) {
    return <ReadOnlyDropDownTree value={value} textField={textField} data-testid={dataTestId} />;
  }

  const handleChange = (e) => {
    const { value: newValue } = e;
    // Reset to the current value if selected item is not selectable
    if (newValue?.selectable === false) {
      onChange({ ...e, value: value ?? null, target: e.target ? { ...e.target, value: value ?? null } : { value: value ?? null } });
      return;
    }
    onChange(e);
  };

  const TreeComponent = multiSelect ? MultiSelectTree : DropDownTree;

  return (
    <span data-testid={dataTestId}>
      <TreeComponent
        ref={ref}
        {...props}
        data={treeData}
        onChange={handleChange}
        onExpandChange={handleExpandChange}
        opened={!props.disabled && !readOnly ? undefined : false}
        data-testid={dataTestId}
      />
    </span>
  );
};

/**
 * A base dropdown tree component with various options and handlers.
 *
 * @param {TelerikDropDownTreeProps & TelerikMultiSelectTreeProps & AdditionalDropDownTreeProps} props Component props.
 * @param {React.Ref<HTMLDivElement>} ref
 * @returns {React.ReactElement} Dropdown tree component.
 */
const ForwardDropDownTree = forwardRef(BaseDropDownTree);

export default ForwardDropDownTree;
